import React, { FC, Fragment, ReactNode, useEffect } from 'react';
import Modal from 'components/shared/Modal';
import { Button } from '@kontentino/ui';
import useBoolean from 'utils/hooks/useBoolean';
import {
  unstable_BlockerFunction as BlockerFunction,
  unstable_useBlocker as useBlocker,
  useSearchParams,
} from 'react-router-dom';
import i18n from 'app/utils/i18n';

type Props = {
  shouldBlock: BlockerFunction;
  children: ReactNode;
  variant: 'create' | 'edit';
  type: 'post' | 'inspiration' | 'contentPlan' | 'profileGroup';
  isSubmitting?: boolean;
  onSubmit: ({
    onSuccess,
    onError,
  }: {
    onSuccess(): void;
    onError(): void;
  }) => void;
};

const SUBMIT_SUCCESSFUL_QUERY_PARAM = 'submitSuccessful';
const COMPOSE_QUERY_PARAM = 'compose';

export const useUnsavedChangesHandler = () => {
  const setSearchParams = useSearchParams()[1];

  return async (func: (...args: any) => Promise<any>) => {
    setSearchParams((prevParams) => {
      prevParams.append(SUBMIT_SUCCESSFUL_QUERY_PARAM, 'true');
      return prevParams;
    });
    await func();
    setSearchParams((prevParams) => {
      prevParams.delete(SUBMIT_SUCCESSFUL_QUERY_PARAM);
      prevParams.delete(COMPOSE_QUERY_PARAM);
      return prevParams;
    });
  };
};

const TRANS_PREFIX = 'unsavedChanges';

const ON_SUBMIT_TRANS_KEY_MAP = {
  post: {
    create: 'onSaveAsDraft',
    edit: 'onSaveChanges',
  },
  inspiration: {
    create: 'onSave',
    edit: 'onSaveChanges',
  },
  contentPlan: {
    create: 'onSave',
    edit: 'onSaveChanges',
  },
  profileGroup: {
    create: 'onSave',
    edit: 'onSaveChanges',
  },
};

const UnsavedChangesHandler: FC<Props> = ({
  shouldBlock,
  type,
  variant,
  children,
  isSubmitting,
  onSubmit,
}) => {
  const [isOpen, setIsOpen] = useBoolean(false);
  const texts = {
    header: i18n.t(`${TRANS_PREFIX}.${type}.${variant}.header`),
    content: i18n.t(`${TRANS_PREFIX}.${type}.${variant}.content`),
    onLeaveWithoutSaving: i18n.t(`${TRANS_PREFIX}.onLeaveWithoutSaving`),
    onSubmit: i18n.t(
      `${TRANS_PREFIX}.${ON_SUBMIT_TRANS_KEY_MAP[type][variant]}`,
    ),
  };

  const blocker = useBlocker((args) => {
    const nextSubmitSuccessful = new URLSearchParams(
      args.nextLocation.search,
    ).get(SUBMIT_SUCCESSFUL_QUERY_PARAM);
    const currentSubmitSuccessful = new URLSearchParams(
      args.currentLocation.search,
    ).get(SUBMIT_SUCCESSFUL_QUERY_PARAM);

    const submitSuccessful = nextSubmitSuccessful ?? currentSubmitSuccessful;

    const block = shouldBlock(args) && submitSuccessful !== 'true';
    if (block) {
      setIsOpen.on();
    }
    return block;
  });

  const onLeaveWithoutSaving = () => {
    setIsOpen.off();
    blocker.proceed?.();
  };

  const _onSubmit = () => {
    onSubmit({
      onSuccess: () => {
        setIsOpen.off();
        blocker.proceed?.();
      },
      onError: () => {
        setIsOpen.off();
      },
    });
  };

  const onEscKeyDown = (e: KeyboardEvent) => {
    // workaround for ecsape keydown closing both modals
    if (e.key === 'Escape' && isOpen) {
      e.stopPropagation();
      setIsOpen.off();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', onEscKeyDown);
    return () => {
      document.removeEventListener('keydown', onEscKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Fragment>
      {children}
      <Modal
        focusTrapped
        open={isOpen}
        onEscKeyDown={() => {
          // workaround for ecsape keydown closing both modals
        }}
        onClose={setIsOpen.off}
        styles={{ modal: { width: 400 } }}
      >
        <Modal.Header className="tw-pb-1" title={texts.header} />
        <Modal.Content>
          <p className="tw-pb-4 tw-text-md tw-text-grayscale-140">
            {texts.content}
          </p>
        </Modal.Content>
        <Modal.Footer className="tw-border-t tw-border-grayscale-20">
          <Button variant="secondary" onClick={onLeaveWithoutSaving}>
            {texts.onLeaveWithoutSaving}
          </Button>
          <Button
            variant="primary"
            onClick={_onSubmit}
            isLoading={isSubmitting}
          >
            {texts.onSubmit}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default UnsavedChangesHandler;
